import URIJS from 'urijs';
import baseUrl from './baseurl.js';


/**
 * Wrapper around urijs to allow easy URI manipulation
 * @author Geoffrey Mayeux
 * @category url
 */
class URI{
	/**
	 * Create a new URI instance
	 * @param {string} uri The URI string
	 */
	constructor(uri){
		this._uri 	= URIJS(uri);
		this._base 	= URIJS(baseUrl());

		this._uri.normalize();
		this._base.normalize();
	}

	/**
	 * Get the base url
	 * @returns {string} The base url
	 */
	get base() 			{ return this._base; };

	/**
	 * Get the relative path of the uri from the current base url
	 * @returns {string} The relative path
	 */
	get relative() 		{ return this._uri.toString().replace(this._base.toString(), ""); }

	/**
	 * Check whether the url is external
	 * @returns {boolean} true if the url is external
	 */
	get external()		{ return this.hostname != this._base.hostname(); }

	/**
	 * Get the scheme of the url
	 * @returns {string} The scheme
	 */
	get scheme()		{ return this._uri.scheme() || this._base.scheme(); }
	/**
	 * Set the scheme of the url
	 * @param {string} scheme The scheme
	 */
	set scheme(value)	{ this._uri.scheme(value); }

	/**
	 * Get the hostname of the url
	 * @returns {string} The hostname
	 */
	get hostname()		{ return this._uri.hostname() || this._base.hostname(); }
	/**
	 * Set the hostname of the url
	 * @param {string} hostname The hostname
	 */
	set hostname(value)	{ this._uri.hostname(value); }

	/**
	 * Get the port of the url
	 * @returns {string} The port
	 */
	get port()			{ return this._uri.port(); }
	/**
	 * Set the port of the url
	 * @param {string} port The port
	 */
	set port(value)		{ this._uri.port(value); }

	/**
	 * Get the username of the url
	 * @returns {string} The username
	 */
	get username()		{ return this._uri.username(); }
	/**
	 * Set the username of the url
	 * @param {string} username The username
	 */
	set username(value)	{ this._uri.username(value); }

	/**
	 * Get the password of the url
	 * @returns {string} The password
	 */
	get password()		{ return this._uri.password(); }
	/**
	 * Set the password of the url
	 * @param {string} password The password
	 */
	set password(value)	{ this._uri.password(value); }
	
	/**
	 * Get the path of the url
	 * @returns {string} The path
	 */
	get path()			{ return this._uri.path(); }
	/**
	 * Set the path of the url
	 * @param {string} path The path
	 */
	set path(value)		{ this._uri.path(value); }
	
	/**
	 * Get the Top-Level-Domain of the url
	 * @returns {string} The Top-Level-Domain
	 */
	get tld()			{ return this._uri.tld(); }
	/**
	 * Set the Top-Level-Domain of the url
	 * @param {string} port The Top-Level-Domain
	 */
	set tld(value)		{ this._uri.tld(value); }
	
	/**
	 * Get the hash of the url
	 * @returns {string} The hash
	 */
	get hash()			{ return this._uri.hash(); }
	/**
	 * Set the hash of the url
	 * @param {string} port The hash
	 */
	set hash(value)		{ this._uri.hash(value); }

	/**
	 * Get the segment of the url
	 * @returns {string} The segment
	 */
	get segment()		{ return this._uri.segment(); }
	/**
	 * Set the segment of the url
	 * @param {string} port The segment
	 */
	set segment(value)	{ this._uri.segment(value); }

	/**
	 * Get the query of the url
	 * @returns {string} The query
	 */
	get query()			{ return this._uri.query(true); }
	/**
	 * Set the query of the url
	 * @param {string} port The query
	 */
	set query(value)	{ this._uri.query(value); }

	equals(url = undefined){
		if(url instanceof URI)
			return this._uri.equals(url._uri);
		return this._uri.equals(url.toString());
	}

	toString(){
		return this._uri.toString();
	}
}
export default URI;
export { URI };